import React, { useState } from "react";
import { useMutation, useQueryClient } from "react-query";

import useAllPlayers from "../../../../../hooks/queryResults/useAllPlayers";
import { SKIN_TONES } from "../../../../../constants/general";
import useUrls from "../../../../../hooks/useUrls";
import http from "../../../../../services/httpService";
import Util from "../../../../../util/util";
import Loading from "../../../../ui/Loading";

const skinToneColors = [
  "#ff0080",
  "#2f1c1a",
  "#432c28",
  "#8f675f",
  "#b59389",
  "#f1d3c7",
];

/**
 *
 * @param player
 * @param skinTones
 * @param setSkinTones
 * @returns {JSX.Element}
 * @constructor
 */
function PlayerRow({ player, skinTones, setSkinTones }) {
  const [skinTone, setSkinTone] = useState(player.skinTone);

  const skinToneNames = [
    "unknown",
    "dark",
    "medium dark",
    "medium",
    "medium light",
    "light",
  ];

  const toggleSkinTone = () => {
    const newSkinTone = (skinTone + 1) % 6;
    changeSkinTone(newSkinTone);
  };

  const changeSkinTone = (newSkinTone) => {
    setSkinTone(newSkinTone);
    setSkinTones({ ...skinTones, [player.id]: newSkinTone });
  };

  const { pos, firstName, lastName, nflTeamAbbr } = player;

  let fpImg = null;
  if (player.fpId) {
    fpImg = (
      <img
        style={{ backgroundColor: skinToneColors[skinTone] }}
        src={`https://images.fantasypros.com/images/players/nfl/${player.fpId}/headshot/80x80.webp`}
        alt={`${player.firstName} ${player.lastName}`}
      />
    );
  }

  let cbsImg = null;
  if (player.cbsId) {
    cbsImg = (
      <img
        style={{ backgroundColor: skinToneColors[skinTone] }}
        src={`https://sports.cbsimg.net/images/football/nfl/players/60x80/${player.cbsId}.jpg`}
        alt={`${player.firstName} ${player.lastName}`}
      />
    );
  }

  const skin = <span>{skinToneNames[skinTone]}</span>;

  const swatches = (
    <div style={{ display: "flex", width: "6rem", flexWrap: "wrap" }}>
      {skinToneColors.map((skinToneColor, i) => {
        const active = i === skinTone;
        const borderColor = active ? "yellow" : skinToneColor;
        return (
          <div
            onClick={() => changeSkinTone(i)}
            style={{
              width: "2rem",
              height: "2rem",
              backgroundColor: skinToneColor,
              border: `3px solid ${borderColor}`,
            }}
          />
        );
      })}
    </div>
  );

  return (
    <tr style={{ cursor: "pointer" }}>
      <td onClick={() => toggleSkinTone(player.id)}>{fpImg}</td>
      <td onClick={() => toggleSkinTone(player.id)}>{cbsImg}</td>
      <td
        onClick={(e) => {
          e.preventDefault();
          toggleSkinTone(player.id);
        }}
        style={{ backgroundColor: skinToneColors[skinTone], color: "white" }}
      >
        {skin}
      </td>
      <td>{swatches}</td>
      <td>{pos}</td>
      <td>{firstName}</td>
      <td>{lastName}</td>
      <td>{nflTeamAbbr}</td>
    </tr>
  );
}

/**
 *
 * @returns {JSX.Element}
 * @constructor
 */
export default function AdminRemoteAllPlayersTable() {
  const url = "/players/all";
  const { playersData, playersQuery } = useAllPlayers();
  const [filter, setFilter] = useState(SKIN_TONES.UNKNOWN);
  const queryClient = useQueryClient();
  const { apiUrl } = useUrls();

  const [skinTones, setSkinTones] = useState({});

  const mutation = useMutation(
    () => {
      const data = { skinTones };

      const url = `${apiUrl}/admin/players/skin-tones`;

      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with data:`, data);
      return http.patch(url, data, config);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(url);
        await queryClient.refetchQueries(url);
        setSkinTones({});
      },
      onError: (err) => {
        console.error(err);
      },
    }
  );

  const borderColor = Object.keys(skinTones).length >= 50 ? "red" : "white";

  return playersQuery.isLoading ? (
    <Loading />
  ) : (
    <>
      <button onClick={mutation.mutate} className={"btn"}>
        Update
      </button>
      <p>{JSON.stringify(skinTones)}</p>
      <div style={{ display: "flex", alignItems: "center", margin: "1rem 0" }}>
        Filters:{" "}
        {skinToneColors.map((skinToneColor, i) => {
          const active = i === filter;
          const borderColor = active ? "yellow" : skinToneColor;
          return (
            <div
              onClick={() => setFilter(i)}
              style={{
                width: "2rem",
                height: "2rem",
                backgroundColor: skinToneColor,
                border: `3px solid ${borderColor}`,
                margin: "0.25rem",
                cursor: "pointer",
              }}
            />
          );
        })}
        <div
          onClick={() => setFilter("all")}
          style={{
            padding: "0.5rem",
            border: `3px solid ${filter === "all" ? "yellow" : "transparent"}`,
            margin: "0.25rem",
            cursor: "pointer",
          }}
        >
          All
        </div>
      </div>
      <table style={{ border: `20px solid ${borderColor}` }}>
        <thead>
          <tr>
            <th>FP</th>
            <th>CBS</th>
            <th>Skin</th>
            <th>Swatches</th>
            <th>Pos</th>
            <th>First</th>
            <th>Last</th>
            <th>Team</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(playersData.players).map((playerId, i) => {
            const player = playersData.players[playerId];
            if (
              filter !== "all" &&
              (player.pos === "DEF" || player.skinTone !== filter)
            )
              return null;
            return (
              <PlayerRow
                key={i}
                player={player}
                skinTones={skinTones}
                setSkinTones={setSkinTones}
              />
            );
          })}
        </tbody>
      </table>
    </>
  );
}
